import React from "react";
import { Link } from "react-router-dom";
import { Play, Bookmark, Plus, Clock } from "lucide-react";
import { usePlayer, fmtTime } from "../context/PlayerContext";
import { toast } from "sonner";

// A single lecture card used across the app.
// item: { id, title, creator, thumbnail, downloads, series }
export default function LectureCard({ item, featured = false, showSeries = true }) {
  const { addToQueue, playTracks, toggleSaved, isSaved } = usePlayer();

  const quickPlay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item/${encodeURIComponent(item.id)}`);
      const data = await res.json();
      if (!data.tracks?.length) { toast.error("No playable audio"); return; }
      const tracks = data.tracks.map(t => ({
        id: `${data.id}::${t.name}`,
        title: t.title,
        creator: data.creator,
        thumbnail: data.thumbnail,
        url: t.url,
        length: t.length,
        parent: data.id,
        parentTitle: data.title,
      }));
      playTracks(tracks, 0);
    } catch { toast.error("Failed to load track"); }
  };

  const quickQueue = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item/${encodeURIComponent(item.id)}`);
      const data = await res.json();
      if (!data.tracks?.length) { toast.error("No playable audio"); return; }
      const t = data.tracks[0];
      addToQueue({
        id: `${data.id}::${t.name}`,
        title: t.title,
        creator: data.creator,
        thumbnail: data.thumbnail,
        url: t.url,
        length: t.length,
        parent: data.id,
        parentTitle: data.title,
      });
    } catch { toast.error("Failed to queue"); }
  };

  return (
    <Link
      to={`/item/${encodeURIComponent(item.id)}`}
      data-testid={`lecture-card-${item.id}`}
      className={`archival-card ${featured ? "featured" : ""} rounded-lg p-3 sm:p-4 flex gap-3 sm:gap-4 group`}
    >
      <div className="relative shrink-0 w-[72px] h-[72px] sm:w-[92px] sm:h-[92px] rounded-md overflow-hidden bg-black/40 border border-white/5">
        <img
          src={item.thumbnail}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = 0.15; }}
          className={`w-full h-full ${item.cover_fit === "contain" ? "object-contain" : "object-cover"}`}
        />
        <button
          onClick={quickPlay}
          data-testid={`quick-play-${item.id}`}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Play"
        >
          <span className="play-btn !w-11 !h-11"><Play className="w-4 h-4 ml-0.5 fill-current" /></span>
        </button>
      </div>

      <div className="min-w-0 flex-1">
        {showSeries && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gold text-[11px] font-semibold uppercase tracking-[0.14em]">
              {item.series || "Lecture"}
            </span>
            {typeof item.downloads === "number" && item.downloads > 0 && (
              <span className="text-white/40 text-xs">• {item.downloads.toLocaleString()} plays</span>
            )}
          </div>
        )}
        <h3 className="font-display text-[16px] sm:text-[19px] leading-snug text-paper truncate group-hover:text-emerald-soft transition-colors">
          {item.title}
        </h3>
        <p className="text-xs sm:text-sm text-white/60 mt-0.5 truncate">{item.creator}</p>
        {item.summary && (
          <p className="text-[13px] text-white/55 mt-2 leading-relaxed line-clamp-2 italic">{item.summary}</p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button onClick={quickQueue} data-testid={`quick-queue-${item.id}`} className="chip">
            <Plus className="w-3.5 h-3.5" /> Queue
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaved(item); }}
            data-testid={`save-${item.id}`}
            className={`chip ${isSaved(item.id) ? "active" : ""}`}
          >
            <Bookmark className="w-3.5 h-3.5" /> {isSaved(item.id) ? "Saved" : "Save"}
          </button>
          {item.date && (
            <span className="chip"><Clock className="w-3.5 h-3.5" /> {String(item.date).slice(0, 4)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
